import React from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack, TextField, MenuItem, Select, Divider, Chip } from '@mui/material';
import { Folders } from '@phosphor-icons/react';

export type ChecklistValue = 'yes' | 'no' | 'unknown';

export interface ChecklistState {
  selfHarm?: ChecklistValue;
  medications?: string[] | null;
  allergies?: string[] | null;
  enemiesGang?: ChecklistValue;
  selectedCollectionId?: string | null;
}

export interface CollectionOption {
  id: string;
  title: string;
  referenceNumber?: string | null;
}

interface EndSessionChecklistDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (state: ChecklistState) => void;
  state: ChecklistState;
  setState: (s: ChecklistState) => void;
  // Optional collection props
  availableCollections?: CollectionOption[];
  currentCollections?: CollectionOption[];
  isLoadingCollections?: boolean;
}

export const EndSessionChecklistDialog: React.FC<EndSessionChecklistDialogProps> = ({ 
  open, 
  onClose, 
  onGenerate, 
  state, 
  setState,
  availableCollections = [],
  currentCollections = [],
  isLoadingCollections = false
}) => {
  const setChip = (key: keyof ChecklistState, value: ChecklistValue) => {
    setState({ ...state, [key]: value });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '16px', color: '#111827', pb: 1 }}>
        Verify critical items
      </DialogTitle>
      
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ mb: 3, fontSize: '13px', color: '#6b7280' }}>
          AI detections may arrive a few seconds late. Please confirm or mark unknown.
        </Typography>

        <Stack spacing={2.5}>
          {/* Self-harm risk */}
          <Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 1 }}>
              Self-harm risk
            </Typography>
            <Stack direction="row" spacing={0.75}>
              {(['yes', 'no', 'unknown'] as const).map((value) => (
                <Box
                  key={value}
                  onClick={() => setChip('selfHarm', value)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: state.selfHarm === value ? (value === 'yes' ? '#dc2626' : '#d1d5db') : '#e5e7eb',
                    backgroundColor: state.selfHarm === value ? (value === 'yes' ? '#fee2e2' : '#f3f4f6') : 'white',
                    color: state.selfHarm === value ? (value === 'yes' ? '#dc2626' : '#374151') : '#6b7280',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: state.selfHarm === value ? undefined : '#f9fafb',
                      borderColor: '#d1d5db'
                    }
                  }}
                >
                  {value.charAt(0).toUpperCase() + value.slice(1)}
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Medications */}
          <Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 1 }}>
              Medications
            </Typography>
            <TextField
              placeholder="e.g., sertraline, insulin"
              fullWidth
              size="small"
              value={(state.medications || []).join(', ')}
              onChange={(e) => setState({ ...state, medications: e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : [] })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '13px',
                  '& fieldset': {
                    borderColor: '#e5e7eb'
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d5db'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6b7280',
                    borderWidth: '1px'
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '13px',
                  color: '#374151'
                }
              }}
            />
          </Box>

          {/* Allergies */}
          <Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 1 }}>
              Allergies
            </Typography>
            <TextField
              placeholder="e.g., penicillin, peanuts"
              fullWidth
              size="small"
              value={(state.allergies || []).join(', ')}
              onChange={(e) => setState({ ...state, allergies: e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : [] })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '13px',
                  '& fieldset': {
                    borderColor: '#e5e7eb'
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d5db'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6b7280',
                    borderWidth: '1px'
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '13px',
                  color: '#374151'
                }
              }}
            />
          </Box>

          {/* Enemies/Gang */}
          <Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 1 }}>
              Enemies/Gang affiliation
            </Typography>
            <Stack direction="row" spacing={0.75}>
              {(['yes', 'no', 'unknown'] as const).map((value) => (
                <Box
                  key={value}
                  onClick={() => setChip('enemiesGang', value)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: state.enemiesGang === value ? (value === 'yes' ? '#7c3aed' : '#d1d5db') : '#e5e7eb',
                    backgroundColor: state.enemiesGang === value ? (value === 'yes' ? '#ede9fe' : '#f3f4f6') : 'white',
                    color: state.enemiesGang === value ? (value === 'yes' ? '#7c3aed' : '#374151') : '#6b7280',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: state.enemiesGang === value ? undefined : '#f9fafb',
                      borderColor: '#d1d5db'
                    }
                  }}
                >
                  {value.charAt(0).toUpperCase() + value.slice(1)}
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Divider */}
          <Divider sx={{ my: 1 }} />

          {/* Collections */}
          <Box sx={{ px: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
              <Folders size={16} weight="regular" style={{ color: '#6b7280' }} />
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                Add to Folder <Typography component="span" sx={{ fontSize: '11px', fontWeight: 400, color: '#9ca3af' }}>(Optional)</Typography>
              </Typography>
            </Stack>
            
            {/* Show current collections if any */}
            {currentCollections.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: '11px', color: '#6b7280', mb: 0.5 }}>
                  Currently in:
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {currentCollections.map((col) => (
                    <Chip
                      key={col.id}
                      label={col.title}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '11px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Select
              fullWidth
              size="small"
              displayEmpty
              value={state.selectedCollectionId || ''}
              onChange={(e) => setState({ ...state, selectedCollectionId: e.target.value || null })}
              disabled={isLoadingCollections}
              sx={{
                borderRadius: '6px',
                fontSize: '13px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e5e7eb'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d5db'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6b7280',
                  borderWidth: '1px'
                },
                '& .MuiSelect-select': {
                  fontSize: '13px',
                  color: '#374151',
                  py: 1
                }
              }}
            >
              <MenuItem value="" sx={{ fontSize: '13px', color: '#9ca3af' }}>
                {isLoadingCollections ? 'Loading collections...' : currentCollections.length > 0 ? 'Add to another collection...' : 'Select a collection...'}
              </MenuItem>
              {availableCollections
                .filter(col => !currentCollections.some(current => current.id === col.id))
                .map((collection) => (
                  <MenuItem 
                    key={collection.id} 
                    value={collection.id}
                    sx={{ fontSize: '13px' }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: '#374151' }}>
                        {collection.title}
                      </Typography>
                      {collection.referenceNumber && (
                        <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                          {collection.referenceNumber}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
            </Select>
          </Box>
        </Stack>

        <Typography variant="caption" sx={{ display: 'block', mt: 2, fontSize: '11px', color: '#9ca3af' }}>
          All confirmations are recorded in the audit log.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button 
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 500,
            color: '#6b7280',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#f3f4f6'
            }
          }}
        >
          Back to session
        </Button>
        <Button 
          onClick={() => onGenerate(state)}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 500,
            backgroundColor: '#2563eb',
            color: 'white',
            borderRadius: '6px',
            px: 2,
            '&:hover': {
              backgroundColor: '#1d4ed8'
            }
          }}
        >
          Generate
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EndSessionChecklistDialog;


